import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFelaEnhanced } from '@ackee/fela';

import * as UI from '../../ui';
import Config from 'config';
import { BackgroundMode, PixelatedBackgroundContext } from '../PixelatedBackgroundWrapper';

import deloitte from '../../../../static/images/hp/awards/img_award_deloitte_fast_500.svg';

import germanBrand from '../../../../static/images/hp/awards/GBA_logo.svg';
import germanBrandWhite from '../../../../static/images/hp/awards/GBA_logo_white.svg';
import multimedia from '../../../../static/images/hp/awards/Annual_Multimedia_logo.svg';
import multimediaWhite from '../../../../static/images/hp/awards/Annual_Multimedia_logo_white.svg';

import deloitteBVariant from '../../../../static/images/hp/awards/img_award_deloitte_fast_500_b.svg';
import germanBrandBVariant from '../../../../static/images/hp/awards/GBA_logo_b.svg';
import multimediaBVariant from '../../../../static/images/hp/awards/Annual_Multimedia_logo_b.svg';

import * as felaRules from './OurAwards.rules';

const { DynamicContainer, ContainerType, Title, TitleTypes, Text, TextTypes } = UI;

interface LinkData {
  link: {
    cs?: string;
    de?: string;
    en?: string;
  };
  alt: string;
  image0: string;
  image1: string;
  image0white: string;
  image1white: string;
}

interface LinksData {
  [key: string]: LinkData;
}

const handleClick = (): void => {
  if (window) {
    (window as any).ga('send', 'event', {
      eventCategory: 'Link',
      eventAction: 'click',
      eventLabel: 'awards_click_on_award',
      transport: 'beacon',
    });
  }
};

export const OurAwards = forwardRef<HTMLElement>((props, ref) => {
  const { formatMessage, locale } = useIntl();
  const backgroundMode = useContext(PixelatedBackgroundContext);
  const { rules, styles, theme } = useFelaEnhanced(felaRules, { backgroundMode });

  const deloitteAlt = formatMessage({ id: 'homepage.awards.logo.alt.deloitte' });
  const germanBrandAlt = formatMessage({ id: 'homepage.awards.logo.alt.germanBrand' });
  const multimediaAlt = formatMessage({ id: 'homepage.awards.logo.alt.multimedia' });

  const links: LinksData = {
    deloitte: {
      link: {
        cs: 'https://www.ackee.cz/blog/ackee-v-deloitte-technology-fast-500-emea/',
        de: undefined,
        en: 'https://www.ackee.agency/blog/ackee-in-technology-fast-500-emea/',
      },
      alt: deloitteAlt,
      image0: deloitte,
      image1: deloitteBVariant,
      image0white: deloitte,
      image1white: deloitteBVariant,
    },
    gba: {
      link: {
        cs: 'https://www.ackee.cz/blog/aplikace-pro-bundestag-ziskala-german-brand-award/',
        de: undefined,
        en: 'https://www.ackee.agency/blog/bundestag-app-receives-german-brand-award/',
      },
      alt: germanBrandAlt,
      image0: germanBrand,
      image1: germanBrandBVariant,
      image0white: germanBrandWhite,
      image1white: germanBrandWhite,
    },
    multimedia: {
      link: {
        cs: 'https://www.ackee.cz/blog/ceska-aplikace-pro-bundestag-sbira-v-nemecku-oceneni/',
        de: 'https://www.annual-multimedia.de/hall-of-fame-2020-silber',
        en: undefined,
      },
      alt: multimediaAlt,
      image0: multimedia,
      image1: multimediaBVariant,
      image0white: multimediaWhite,
      image1white: multimediaWhite,
    },
  };

  const [abTestVariant, setAbTestVariant] = useState<string | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if ((window as any).google_optimize !== undefined) {
        const variant = (window as any).google_optimize.get(Config.experiments.awards.id);
        setAbTestVariant(variant);
        clearInterval(intervalId);
      }
    }, 100);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDark = backgroundMode === BackgroundMode.Dark;

  const renderLinks = (variant: string) =>
    Object.entries(links).map(([key, value]) => {
      if (value.link[locale as keyof typeof value.link]) {
        return (
          <a
            onClick={handleClick}
            key={key}
            rel="noopener noreferrer"
            target="_blank"
            href={value.link[locale as keyof typeof value.link]}
          >
            {/* @ts-expect-error */}
            <img className={`${styles[key]} ${isDark ? styles.white : ''}`} alt={value.alt} src={value[`image${variant}${isDark ? 'white' : ''}` as keyof typeof value]} />
          </a>
        );
      }
      {/* @ts-expect-error */}
      return <img key={key} className={`${styles[key]} ${isDark ? styles.white : ''}`} alt={value.alt} src={value[`image${variant}${isDark ? 'white' : ''}` as keyof typeof value]} />;
    });

  return (
    <DynamicContainer extend={rules.extendContainer} isFluid type={ContainerType.GRID} ref={ref} backgroundMode={backgroundMode}>
{/* @ts-expect-error */}
      <Text columns={12} type={TextTypes.SECTION} extend={{text: rules.extendTitle}} backgroundMode={backgroundMode}>
        <FormattedMessage id="homepage.awards.sectionTitle" />
      </Text>
      <DynamicContainer columns={4}>
        {/* @ts-expect-error */}
        <Title as={TitleTypes.H2} type={TitleTypes.H3} extend={{title: rules.extendTitle}} backgroundMode={backgroundMode}>
          <FormattedMessage id="homepage.awards.title" />
        </Title>
      </DynamicContainer>
      {/* @ts-expect-error */}
      <DynamicContainer columns={8} extend={{container: rules.extendLogoContainer}}>
        <div className={styles.divider} />
        {abTestVariant && renderLinks(abTestVariant)}
        {Boolean(abTestVariant) === false && renderLinks('0')} 
      </DynamicContainer>
    </DynamicContainer>
  );
});

export default OurAwards; 